import { TextArea } from '@thanx/uikit/textarea'
import { useStyletron } from '@thanx/uikit/theme'
import { Toggle } from '@thanx/uikit/toggle'
import guestAcknowledgmentMockup from 'assets/images/order-feedback/guest-acknowledgment-message-phone-mockup.png'
import guestCallTheStoreMockup from 'assets/images/order-feedback/guest-call-the-store-phone-mockup.png'
import Section from 'components/Section'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import CmsPage from 'scenes/Cms/components/CmsPage'
import { selectCurrentMerchant } from 'selectors/merchant'

import useDispatch from 'hooks/useDispatch'
import {
  setAcknowledgmentMessage,
  setCallTheStoreEnabled,
} from 'slices/orderFeedbackGuestExperience'
import {
  MAX_ACKNOWLEDGMENT_LENGTH,
  selectAcknowledgmentMessage,
  selectCallTheStoreEnabled,
} from 'slices/orderFeedbackGuestExperience/selectors'
import AppVersionNotice from './AppVersionNotice'
import { useFocusWrittenFeedbackToggle } from './hooks/useFocusWrittenFeedbackToggle'
import { useGuestExperienceFormInitialize } from './hooks/useGuestExperienceFormInitialize'
import { useOwningFeedbackToolName } from './hooks/useOwningFeedbackToolName'
import { useSettingsEditPermission } from './hooks/useSettingsEditPermission'
import { useWrittenFeedbackPersisted } from './hooks/useWrittenFeedbackEnabled'
import { PANE_TITLE_STYLE } from './paneTitleStyle'
import WrittenFeedbackGateNote from './WrittenFeedbackGateNote'

const t = buildTranslate('order_ratings.settings')
const tRow = buildTranslate('order_ratings.settings.written_feedback_gate.row')

const PANE_GATE_NOTE_ID = 'guest-experience-gate-note'
const THIRD_PARTY_NOTE_ID = 'guest-experience-third-party-note'
const ROW_GATE_NOTE_ID = 'call-the-store-gate-note'
const ACKNOWLEDGMENT_TEXTAREA_ID = 'acknowledgment-message-input'
const ACKNOWLEDGMENT_HINT_ID = 'acknowledgment-message-hint'
const CALL_THE_STORE_TOGGLE_ID = 'call-the-store-toggle'

// Measured off prototype v67's own computed styles (R1 sec 2.3) — the same
// 4px-radius bordered-card spec ReplyAddressPane already measured for its own
// card (R1 sec 1.3 is identical), reused here rather than re-derived.
const CARD_BORDER_COLOR = palette.grey30
const CARD_RADIUS = '4px'
const CARD_PADDING = '6px 24px'
const ROW_PADDING = '22px 0'
const DIVIDER_COLOR = palette.grey10
const HEADING_FONT_SIZE = '15px'
const HEADING_LINE_HEIGHT = '22.5px'
const BODY_FONT_SIZE = '13.5px'
const BODY_LINE_HEIGHT = '20.25px'
// Measured on v70: `textarea.reply` computes `max-width: none; width: 100%`.
// The 10px is the design's own gap between the row description and the field.
const TEXTAREA_MARGIN_TOP = '10px'

// R1 sec 2.2's phone-mockup illustration for each row (`.illus-box`). Live
// computed styles pulled from v70 by design-audit-1283 (AI-1283 illustrations
// follow-up), identical on both rows. `.illus-box` is a plain 162x138 clipped
// window with no border/background of its own; `.ph` inside it is the actual
// phone bezel — a 142x122 (border-box) frame with a 6px dark border on three
// sides only (top/left/right, none on the bottom) and a 24px top-corner
// radius, so the device appears to run off the bottom edge. `.ph`'s own
// `overflow: hidden` clips the 269x400 source image, which renders as a plain
// flowed block 130px wide with auto height — no object-fit or crop math; the
// top of the taller image is simply what shows through the shorter frame.
// `.ph` sits 16px down from the box's own top via its own margin-top, and
// `.illus-box` centers it horizontally (its default flex alignment leaves the
// explicit height alone). `.illus-box` itself carries `margin-right: 28px` to
// space it from the text beside it — that margin, not a shared row `gap`, is
// what PhoneMockup below reproduces.
const ILLUS_BOX_WIDTH = '162px'
const ILLUS_BOX_HEIGHT = '138px'
const ILLUS_BOX_RADIUS = '16px'
const ILLUS_BOX_MARGIN_RIGHT = '28px'
const PH_WIDTH = '142px'
const PH_HEIGHT = '122px'
const PH_MARGIN_TOP = '16px'
const PH_BORDER_WIDTH = '6px'
const PH_BORDER_COLOR = '#232C37'
const PH_BORDER_RADIUS = '24px'
const PH_BOX_SHADOW = 'rgba(20, 28, 38, 0.28) 0 10px 24px 0'
const PH_IMAGE_WIDTH = '130px'
// Preserves the pre-existing, already-shipped 24px gap between the text
// column and the toggle on the Call-the-store row. It used to live as the
// row's own CSS `gap`, but the row now has a THIRD child (the illustration)
// ahead of the text — a shared `gap` would apply that same 24px to the
// illustration too and stack with its own measured 28px margin. Moved onto
// the text column's own margin-right instead: same visual result, one pair.
const TEXT_TOGGLE_GAP = '24px'

// Which note explains the inert toggle: the pane-level one when a third-party
// tool owns feedback (that state has no nested note), otherwise the nested
// Call-the-store one, and none at all when the pane is live.
const callTheStoreNoteId = (
  gated: boolean,
  owningToolName: string | null
): string | undefined => {
  if (!gated) return undefined
  return owningToolName ? THIRD_PARTY_NOTE_ID : ROW_GATE_NOTE_ID
}

type PhoneMockupProps = {
  src: string
  alt: string
}

// `.illus-box` > `.ph` > `<img>` per prototype v70 (see the constants above
// for what each value is and where it came from). Built the same way
// WrittenFeedbackCard renders its own phone mockup: plain inline styles, no
// styletron `css()`, longhand border/radius properties only — the values
// themselves differ (this row's own measured 28px/16px/130px), so they are
// NOT copied from that card's v52 numbers.
const PhoneMockup = ({ src, alt }: PhoneMockupProps) => (
  <div
    className="flex-shrink-0 d-flex justify-content-center"
    style={{
      width: ILLUS_BOX_WIDTH,
      height: ILLUS_BOX_HEIGHT,
      borderRadius: ILLUS_BOX_RADIUS,
      overflow: 'hidden',
      marginRight: ILLUS_BOX_MARGIN_RIGHT,
    }}
  >
    <div
      style={{
        width: PH_WIDTH,
        height: PH_HEIGHT,
        marginTop: PH_MARGIN_TOP,
        borderTopWidth: PH_BORDER_WIDTH,
        borderLeftWidth: PH_BORDER_WIDTH,
        borderRightWidth: PH_BORDER_WIDTH,
        borderBottomWidth: '0',
        borderStyle: 'solid',
        borderColor: PH_BORDER_COLOR,
        borderTopLeftRadius: PH_BORDER_RADIUS,
        borderTopRightRadius: PH_BORDER_RADIUS,
        overflow: 'hidden',
        boxShadow: PH_BOX_SHADOW,
      }}
    >
      <img src={src} alt={alt} style={{ width: PH_IMAGE_WIDTH }} />
    </div>
  </div>
)

// FR-56/FR-57. Two independent controls: whether the written-feedback form
// offers a "Call the store" phone shortcut, and what a guest sees right after
// submitting that form.
const GuestExperiencePane = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()

  useGuestExperienceFormInitialize()

  const callTheStoreEnabled = useSelector(selectCallTheStoreEnabled)
  const acknowledgmentMessage = useSelector(selectAcknowledgmentMessage)
  // Both settings on this pane need a minimum guest app version (FR-56/FR-57),
  // so the notice reads the merchant's own published releases straight off the
  // full merchant payload. `selectCurrentMerchant` returns `{}` before the
  // merchant loads, which leaves this `undefined` and the notice silent.
  const { published_app_versions: publishedAppVersions } = useSelector(
    selectCurrentMerchant
  )

  // The PERSISTED value, not the live one: measured on prototype v70, this
  // pane is untouched while the Feedback collection toggle is merely staged,
  // and gates only once Publish lands.
  const writtenFeedbackEnabled = useWrittenFeedbackPersisted()
  const owningToolName = useOwningFeedbackToolName()
  const { editDisabled } = useSettingsEditPermission()
  const focusWrittenFeedbackToggle = useFocusWrittenFeedbackToggle()

  const handleToggleChange = useCallback(
    (checked: boolean) => dispatch(setCallTheStoreEnabled(checked)),
    [dispatch]
  )
  const handleMessageChange = useCallback(
    (value: string) => dispatch(setAcknowledgmentMessage(value)),
    [dispatch]
  )

  // A third-party tool owns feedback only while written feedback is off, so it
  // never gates on its own; it only swaps the pane note, because a merchant
  // whose guests never see the Thanx form does not need to be told to turn
  // written feedback on. Only the written-feedback note also carries the nested
  // Call-the-store note. Defensive: v70 hides the Settings tab for that
  // merchant, so the tool's note covers a deep link or an integration connected
  // mid-session.
  const gated = !writtenFeedbackEnabled
  const paneGateNoteId = owningToolName
    ? THIRD_PARTY_NOTE_ID
    : PANE_GATE_NOTE_ID
  const callTheStoreDescribedBy = callTheStoreNoteId(gated, owningToolName)

  // BOTH rows go inert, not just Call the store. Measured on v70: once
  // `is-gated` is on, `.sp-card` — the whole bordered card, acknowledgment
  // message included — computes to `opacity: 0.5; pointer-events: none`. The
  // fade below matches that; the real `disabled` props are this repo's
  // established idiom for it, and are stricter than the design's CSS-only
  // version, which leaves every control keyboard-reachable and submittable.
  const callTheStoreDisabled = editDisabled || gated
  const acknowledgmentDisabled = editDisabled || gated

  const headingClass = css({
    fontSize: HEADING_FONT_SIZE,
    fontWeight: 700,
    lineHeight: HEADING_LINE_HEIGHT,
    color: palette.grey90,
  })
  const bodyClass = css({
    fontSize: BODY_FONT_SIZE,
    lineHeight: BODY_LINE_HEIGHT,
    color: palette.grey70,
    marginTop: '3px',
  })

  return (
    <CmsPage
      title={t('guest_experience.title')}
      titleClassName={css(PANE_TITLE_STYLE)}
      description={t('guest_experience.subtitle')}
    >
      {gated && (
        <div className="mb-m">
          <WrittenFeedbackGateNote id={paneGateNoteId}>
            {owningToolName ? (
              <>
                {t('guest_experience.third_party_gate_note.prefix')}{' '}
                <b>{owningToolName}</b>
                {t('guest_experience.third_party_gate_note.suffix')}
              </>
            ) : (
              <>
                {t('guest_experience.gate_note.prefix')}{' '}
                <b>{t('written_feedback_gate.phrase')}</b>{' '}
                {t('guest_experience.gate_note.suffix')}
              </>
            )}
          </WrittenFeedbackGateNote>
        </div>
      )}
      <Section
        className="mb-m"
        style={{
          opacity: gated ? 0.5 : 1,
          borderColor: CARD_BORDER_COLOR,
          borderRadius: CARD_RADIUS,
          padding: CARD_PADDING,
        }}
      >
        <div
          className={css({
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: ROW_PADDING,
          })}
        >
          <PhoneMockup
            src={guestCallTheStoreMockup}
            alt={t('guest_experience.call_the_store.image_alt')}
          />
          <div
            className={css({ flex: '1 1 0%', marginRight: TEXT_TOGGLE_GAP })}
          >
            <b className={headingClass}>
              {t('guest_experience.call_the_store.heading')}
            </b>
            <p className={bodyClass}>
              {t('guest_experience.call_the_store.body')}
            </p>
            {/* v83 puts the app-version dependency here and nowhere else: in
                this row's own text column, after the description and before
                the row's gate note. Not gated itself — the dependency is true
                whatever state the pane is in — and it owns its own top
                margin, so it leaves no gap on the merchants it hides from. */}
            <AppVersionNotice publishedAppVersions={publishedAppVersions} />
            {gated && !owningToolName && (
              <div className="mt-xs">
                <WrittenFeedbackGateNote
                  id={ROW_GATE_NOTE_ID}
                  action={{
                    label: tRow('link'),
                    suffix: tRow('suffix'),
                    onClick: focusWrittenFeedbackToggle,
                  }}
                >
                  {tRow('body')}
                </WrittenFeedbackGateNote>
              </div>
            )}
          </div>
          <Toggle
            ariaLabel={t('guest_experience.call_the_store.heading')}
            checked={callTheStoreEnabled}
            disabled={callTheStoreDisabled}
            aria-describedby={callTheStoreDescribedBy}
            onChange={handleToggleChange}
            overrides={{
              Input: { props: { id: CALL_THE_STORE_TOGGLE_ID } },
            }}
          />
        </div>
        <div
          className={css({ height: '1px', backgroundColor: DIVIDER_COLOR })}
        />
        <div
          className={css({
            display: 'flex',
            alignItems: 'flex-start',
            padding: ROW_PADDING,
          })}
        >
          <PhoneMockup
            src={guestAcknowledgmentMockup}
            alt={t('guest_experience.acknowledgment_message.image_alt')}
          />
          <div className={css({ flex: '1 1 0%' })}>
            <label
              htmlFor={ACKNOWLEDGMENT_TEXTAREA_ID}
              className={css({ display: 'block' })}
            >
              <b className={headingClass}>
                {t('guest_experience.acknowledgment_message.heading')}
              </b>
            </label>
            <p className={bodyClass}>
              {t('guest_experience.acknowledgment_message.body')}
            </p>
            {/* No visible counter (v67 has none, R1 gap 2): the 500-char
                bound is enforced by `maxLength` below and stated to
                assistive tech via aria-describedby, rather than shown as an
                on-screen count. */}
            <span id={ACKNOWLEDGMENT_HINT_ID} className="sr-only">
              {t('guest_experience.acknowledgment_message.max_length_hint')}
            </span>
            <TextArea
              id={ACKNOWLEDGMENT_TEXTAREA_ID}
              value={acknowledgmentMessage}
              onChange={handleMessageChange}
              disabled={acknowledgmentDisabled}
              maxLength={MAX_ACKNOWLEDGMENT_LENGTH}
              aria-describedby={ACKNOWLEDGMENT_HINT_ID}
              className={css({ marginTop: TEXTAREA_MARGIN_TOP })}
            />
          </div>
        </div>
      </Section>
    </CmsPage>
  )
}

export default GuestExperiencePane
